import { useState, useEffect, useCallback } from 'react';
import { logout as apiLogout, getCurrentUser } from '../services/api';
import { User } from '../types';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const raw = localStorage.getItem('user');
      if (raw) {
        try {
          JSON.parse(raw) as User;
        } catch {
          localStorage.removeItem('user');
        }
      }

      try {
        const currentUser = await getCurrentUser();
        if (!cancelled) {
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        }
      } catch {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    restoreSession();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    function handleAuthLogout() {
      setUser(null);
    }
    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, []);

  const login = useCallback((user: User) => {
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
  }, []);

  const logout = useCallback(async () => {
    // Tell the backend to record the logout before clearing the session.
    try {
      await apiLogout();
    } catch {
      // ignore — client may already be unauthenticated
    }
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    // Also drop any stashed impersonation session.
    sessionStorage.removeItem('impersonator_user');
    setUser(null);
  }, []);

  return { user, loading, login, logout };
}
